import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Filter, Search, RotateCcw } from "lucide-react";
import { db } from "@/firebase/config";
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc } from "firebase/firestore";
import { Incident } from "./types";
import { format } from "date-fns";

interface CaseManagerProps {
  schoolId: string;
}

import { InterventionDialog } from "./InterventionDialog";

export const CaseManager = ({ schoolId }: CaseManagerProps) => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);

  // ... (useEffects and filters same as before) ...

  useEffect(() => {
    if (!schoolId) return;

    const incidentsRef = collection(db, `schools/${schoolId}/disciplineIncidents`);
    // Basic query, client-side filter for prototype speed
    const q = query(incidentsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Incident));
      setIncidents(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [schoolId]);

  const filteredIncidents = incidents.filter(inc => {
    const matchesStatus = filterStatus === 'all' || inc.status === filterStatus;
    const matchesSearch = inc.studentName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          inc.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          inc.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status: string) => {
      switch(status) {
          case 'new': return <Badge variant="secondary">New Report</Badge>;
          case 'investigating': return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200">Investigating</Badge>;
          case 'adjudicated': return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">Adjudicated</Badge>;
          case 'resolved': return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Resolved</Badge>;
          case 'closed': return <Badge variant="outline">Closed</Badge>;
          default: return <Badge variant="outline">{status}</Badge>;
      }
  };

  return (
    <div className="space-y-4">
      <InterventionDialog 
        schoolId={schoolId} 
        incident={selectedIncident} 
        onClose={() => setSelectedIncident(null)} 
      />

      <div className="flex items-center justify-between gap-4">
        {/* ... Search and Filters ... */}
        <div className="relative flex-1 max-w-sm">
             <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
             <Input 
                placeholder="Search cases..." 
                className="pl-9" 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
             />
        </div>
        <div className="flex items-center gap-2">
            <Button 
                variant={filterStatus === 'all' ? 'default' : 'outline'} 
                size="sm" onClick={() => setFilterStatus('all')}
            >All</Button>
            <Button 
                variant={filterStatus === 'new' ? 'default' : 'outline'} 
                size="sm" onClick={() => setFilterStatus('new')}
            >New</Button>
            <Button 
                variant={filterStatus === 'investigating' ? 'default' : 'outline'} 
                size="sm" onClick={() => setFilterStatus('investigating')}
            >Investigating</Button>
            <Button 
                variant={filterStatus === 'resolved' ? 'default' : 'outline'} 
                size="sm" onClick={() => setFilterStatus('resolved')}
            >Resolved</Button>
        </div>
      </div>

      <div className="rounded-md border bg-white dark:bg-slate-900 overflow-hidden">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {loading ? (
                    <TableRow>
                        <TableCell colSpan={6} className="text-center py-8">Loading cases...</TableCell>
                    </TableRow>
                ) : filteredIncidents.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No cases found matching criteria.</TableCell>
                    </TableRow>
                ) : (
                    filteredIncidents.map(incident => (
                        <TableRow key={incident.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <TableCell className="text-xs text-muted-foreground">
                                {format(new Date(incident.timestamp), 'MMM dd, yyyy')}
                                <div className="text-[10px]">{format(new Date(incident.timestamp), 'HH:mm')}</div>
                            </TableCell>
                            <TableCell className="font-medium">
                                {incident.studentName}
                                <div className="text-xs text-muted-foreground">{incident.studentRegNum}</div>
                            </TableCell>
                            <TableCell>
                                <span className="text-sm">{incident.category}</span>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`text-xs ${
                                    incident.severity === 'critical' ? 'border-red-200 text-red-700 bg-red-50' : 
                                    incident.severity === 'major' ? 'border-orange-200 text-orange-700 bg-orange-50' : 
                                    'border-blue-200 text-blue-700 bg-blue-50'
                                }`}>
                                    {incident.severity}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {getStatusBadge(incident.status)}
                            </TableCell>
                            <TableCell className="text-right">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="h-8 w-8 p-0">
                                            <span className="sr-only">Open menu</span>
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                        <DropdownMenuItem onClick={() => {
                                            // Future: Navigate to detail view
                                        }}>View Details</DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        {incident.status !== 'resolved' && (
                                            <>
                                                <DropdownMenuItem className="text-blue-600" onClick={() => {
                                                    setSelectedIncident(incident);
                                                }}>Assign Intervention</DropdownMenuItem>
                                                <DropdownMenuItem className="text-green-600" onClick={async () => {
                                                    // Quick Resolve
                                                    try {
                                                        const docRef = doc(db, `schools/${schoolId}/disciplineIncidents`, incident.id);
                                                        await updateDoc(docRef, { status: 'resolved' });
                                                    } catch(e) { console.error(e); }
                                                }}>Mark Resolved</DropdownMenuItem>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>
      </div>
    </div>
  );
};
